import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Textarea from '../../components/ui/Textarea';
import TextFieldActions from '../../components/textVersions/TextFieldActions';
import {
  getPromptTemplate,
  improvePromptTemplateContent,
  updatePromptTemplate,
} from '../../api/promptTemplatesAdmin';
import { createTextFieldVersion } from '../../api/textFieldVersions';
import { useTextVersionStack } from '../../hooks/useTextVersionStack';
import { buildTextFieldStackKey, isPersistedUuid } from '../../utils/resourceIds';
import { getApiErrorMessage } from '../../utils/errors';
import ImproveTextModal from '../Onboarding/components/ImproveTextModal';
import ImprovePreviewModal from '../Onboarding/components/ImprovePreviewModal';
import { formatDate } from './constants';

function PromptTemplateEdit() {
  const { slug: slugParam } = useParams();
  const slug = decodeURIComponent(slugParam || '');
  const navigate = useNavigate();
  const textVersionStack = useTextVersionStack();

  const [template, setTemplate] = useState(null);
  const [content, setContent] = useState('');
  const [contentError, setContentError] = useState('');
  const [contentImproveError, setContentImproveError] = useState('');
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isImproveModalOpen, setIsImproveModalOpen] = useState(false);
  const [isImproveModalSubmitting, setIsImproveModalSubmitting] = useState(false);
  const [improveModalError, setImproveModalError] = useState('');
  const [isImprovingContent, setIsImprovingContent] = useState(false);
  const [improvePreview, setImprovePreview] = useState(null);
  const [isApplyingImprovePreview, setIsApplyingImprovePreview] = useState(false);
  const [improvePreviewError, setImprovePreviewError] = useState('');

  const contentStackKey = buildTextFieldStackKey(
    'prompt_template',
    template?.id || 'draft',
    'content'
  );

  const loadTemplate = useCallback(async () => {
    setIsLoadingInitial(true);
    setLoadError('');

    try {
      const data = await getPromptTemplate(slug);
      setTemplate(data);
      setContent(data.content || '');
    } catch (err) {
      setLoadError(getApiErrorMessage(err, 'Não foi possível carregar o prompt.'));
      setTemplate(null);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [slug]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleCancel = () => {
    navigate('/prompt-templates');
  };

  const handleCloseImproveModal = () => {
    if (isImproveModalSubmitting) {
      return;
    }
    setIsImproveModalOpen(false);
    setImproveModalError('');
  };

  const handleOpenImproveModal = () => {
    const draft = content.trim();
    if (!draft) {
      setContentImproveError('Escreva um rascunho antes de melhorar o texto.');
      return;
    }

    setContentImproveError('');
    setImproveModalError('');
    setIsImproveModalOpen(true);
  };

  const handleConfirmImproveModal = async (userConsiderations) => {
    setImproveModalError('');
    setIsImproveModalSubmitting(true);
    setIsImprovingContent(true);

    try {
      const draft = content.trim();
      const { text } = await improvePromptTemplateContent(slug, {
        text: draft,
        userConsiderations: userConsiderations || undefined,
      });
      setImprovePreview({
        kind: 'prompt_content',
        previousText: draft,
        improvedText: text,
        resourceType: 'prompt_template',
        resourceId: template?.id,
        fieldName: 'content',
        userConsiderations: userConsiderations || '',
      });
      setIsImproveModalOpen(false);
    } catch (err) {
      setImproveModalError(
        getApiErrorMessage(err, 'Não foi possível melhorar o texto agora. Tente novamente.')
      );
    } finally {
      setIsImproveModalSubmitting(false);
      setIsImprovingContent(false);
    }
  };

  const handleDiscardImprovePreview = () => {
    if (isApplyingImprovePreview) {
      return;
    }
    setImprovePreview(null);
    setImprovePreviewError('');
  };

  const handleBackToImproveConsiderations = () => {
    if (isApplyingImprovePreview) {
      return;
    }
    setImprovePreview(null);
    setImprovePreviewError('');
    setIsImproveModalOpen(true);
  };

  const handleApplyImprovePreview = async () => {
    if (!improvePreview) {
      return;
    }

    setIsApplyingImprovePreview(true);
    setImprovePreviewError('');

    const { previousText, improvedText, resourceType, resourceId, fieldName, userConsiderations } =
      improvePreview;

    try {
      textVersionStack.push(contentStackKey, previousText);

      if (isPersistedUuid(resourceId)) {
        await createTextFieldVersion({
          resourceType,
          resourceId,
          fieldName,
          content: previousText,
          source: 'ai_improve',
          metadata: {
            user_considerations: userConsiderations || undefined,
          },
        });
      }

      setContent(improvedText);
      setImprovePreview(null);
    } catch (err) {
      setImprovePreviewError(
        getApiErrorMessage(err, 'Não foi possível aplicar a melhoria agora. Tente novamente.')
      );
    } finally {
      setIsApplyingImprovePreview(false);
    }
  };

  const handleUndoContent = () => {
    const previous = textVersionStack.undo(contentStackKey);
    if (previous !== null) {
      setContent(previous);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setContentError('');
    setSubmitError('');

    const trimmedContent = content.trim();
    if (!trimmedContent) {
      setContentError('O conteúdo do prompt não pode ficar vazio.');
      return;
    }

    setIsSubmitting(true);

    try {
      await updatePromptTemplate(slug, { content: trimmedContent });
      navigate('/prompt-templates', {
        replace: true,
        state: { editSuccess: true, slug },
      });
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, 'Não foi possível salvar o prompt.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingInitial) {
    return (
      <div className="surface-card px-4 py-12 text-center">
        <p className="text-content-muted text-sm">Carregando prompt...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-700">{loadError}</p>
          <div className="flex gap-2">
            <button type="button" onClick={handleCancel} className="btn-secondary text-sm px-3 py-2">
              Voltar
            </button>
            <button type="button" onClick={loadTemplate} className="btn-primary text-sm px-3 py-2">
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link
            to="/prompt-templates"
            className="text-sm text-content-muted hover:text-content-secondary"
          >
            ← Voltar para prompts
          </Link>
          <h1 className="text-2xl font-bold text-content-primary mt-2">Editar prompt</h1>
          <p className="text-content-muted text-sm mt-1 font-mono">{template?.slug}</p>
        </div>
      </div>

      <div className="surface-card p-4 sm:p-6 space-y-4">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-content-muted">Slug</dt>
            <dd className="mt-1 font-mono text-content-primary">{template?.slug || '—'}</dd>
          </div>
          <div>
            <dt className="text-content-muted">Categoria</dt>
            <dd className="mt-1 text-content-primary">{template?.category || '—'}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-content-muted">Descrição</dt>
            <dd className="mt-1 text-content-primary">{template?.description || '—'}</dd>
          </div>
          <div>
            <dt className="text-content-muted">Atualizado em</dt>
            <dd className="mt-1 text-content-primary">{formatDate(template?.updated_at)}</dd>
          </div>
        </dl>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="surface-card p-4 sm:p-6">
          <Textarea
            id="prompt-content"
            label="Conteúdo"
            value={content}
            onChange={(event) => {
              setContent(event.target.value);
              if (contentImproveError) {
                setContentImproveError('');
              }
            }}
            rows={16}
            required
            error={contentError}
            hint="Use o ícone de IA no canto do campo para refinar o texto. Alterações entram em vigor após salvar."
            action={{
              iconOnly: true,
              ariaLabel: 'Melhorar conteúdo com IA',
              loading: isImprovingContent || isImproveModalSubmitting,
              disabled: isSubmitting || isImproveModalSubmitting || isApplyingImprovePreview,
              onClick: handleOpenImproveModal,
              error: contentImproveError,
            }}
          />
          <TextFieldActions
            stackKey={contentStackKey}
            canUndo={textVersionStack.canUndo(contentStackKey)}
            onUndo={handleUndoContent}
            resourceType="prompt_template"
            resourceId={template?.id}
            fieldName="content"
            onRestoreFromHistory={setContent}
            disabled={isSubmitting || isImproveModalSubmitting || isApplyingImprovePreview}
          />
        </div>

        {submitError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700">{submitError}</p>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="btn-secondary text-sm px-4 py-2.5"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isImproveModalSubmitting || isApplyingImprovePreview}
            className="btn-primary text-sm px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>

      <ImproveTextModal
        open={isImproveModalOpen}
        kind="prompt_content"
        isSubmitting={isImproveModalSubmitting}
        error={improveModalError}
        onClose={handleCloseImproveModal}
        onSubmit={handleConfirmImproveModal}
      />

      <ImprovePreviewModal
        open={Boolean(improvePreview)}
        kind="prompt_content"
        previousText={improvePreview?.previousText || ''}
        improvedText={improvePreview?.improvedText || ''}
        isApplying={isApplyingImprovePreview}
        error={improvePreviewError}
        onDiscard={handleDiscardImprovePreview}
        onBackToConsiderations={handleBackToImproveConsiderations}
        onApply={handleApplyImprovePreview}
      />
    </div>
  );
}

export default PromptTemplateEdit;
